import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { trpc, type RouterOutputs } from '@/lib/trpc';

type Invitations = RouterOutputs['shopifyConnections']['list']['invitations'];
type Invitation = Invitations[number];

/** §13.1 badge palette. Each colour means exactly one thing; no blue accent. */
type Tone = 'emerald' | 'amber' | 'red' | 'neutral';

const BADGE_BASE = 'border-transparent text-[10px] font-semibold uppercase tracking-wide';
const BADGE_TONE: Record<Tone, string> = {
  emerald: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80',
  amber: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300/80',
  red: 'bg-red-100 text-red-800 ring-1 ring-red-300/80',
  neutral: 'bg-muted text-muted-foreground ring-1 ring-border',
};

/** Installation states that still hold a usable link to the merchant store. */
const LIVE_INSTALLATION_STATUSES = ['initializing', 'active', 'needs_reauth', 'error'];

interface ShopifyConnectionPanelProps {
  readonly locationId: string;
  readonly clientOrganizationId: string;
  readonly clientName: string;
}

interface DisplayOnceInvite {
  readonly invitationId: string;
  readonly pairingCode: string;
  readonly expiresAt: string;
  readonly installUrl: string | null;
  /**
   * Wall clock at the moment the code was handed over. The panel only lets a
   * later list fetch retire the code, so the invalidate that follows creation
   * cannot clear a code the operator has not read yet.
   */
  readonly shownAt: number;
}

interface Presentation {
  readonly label: string;
  readonly tone: Tone;
  readonly detail: string;
  /** In-progress states carry a glyph so colour is never the only signal. */
  readonly inProgress?: boolean;
}

function formatMoment(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Plain words for a Shopify scope string. Operators never see `read_orders`;
 * unknown shapes still degrade to readable text rather than raw grammar.
 */
function scopePhrase(t: TFunction, scope: string): string {
  const separator = scope.indexOf('_');
  const verb = separator > 0 ? scope.slice(0, separator) : '';
  const subject = (separator > 0 ? scope.slice(separator + 1) : scope).replace(/_/g, ' ').trim();
  if (subject.length === 0) return scope.replace(/_/g, ' ');
  if (verb === 'read') return t('shopifyConnection.scopes.read', { subject });
  if (verb === 'write') return t('shopifyConnection.scopes.write', { subject });
  return t('shopifyConnection.scopes.plain', { subject: scope.replace(/_/g, ' ') });
}

function invitationPresentation(
  t: TFunction,
  invitation: Invitation | undefined,
  nowMs: number,
): Presentation {
  if (!invitation) {
    return {
      label: t('shopifyConnection.badge.notConnected'),
      tone: 'neutral',
      detail: t('shopifyConnection.detail.notConnected'),
    };
  }
  const created = formatMoment(invitation.createdAt) ?? '';
  const expires = formatMoment(invitation.expiresAt) ?? '';
  const expiredAlready = Date.parse(invitation.expiresAt) <= nowMs;

  if (invitation.state === 'pending' && !expiredAlready) {
    return {
      label: t('shopifyConnection.badge.inviteSent'),
      tone: 'amber',
      detail: t('shopifyConnection.detail.inviteSent', { expires }),
    };
  }
  if (invitation.state === 'pending' || invitation.state === 'expired') {
    return {
      label: t('shopifyConnection.badge.inviteExpired'),
      tone: 'neutral',
      detail: t('shopifyConnection.detail.inviteExpired', { created, expires }),
    };
  }
  if (invitation.state === 'revoked') {
    return {
      label: t('shopifyConnection.badge.inviteRevoked'),
      tone: 'neutral',
      detail: t('shopifyConnection.detail.inviteRevoked', {
        created,
        revoked: formatMoment(invitation.revokedAt) ?? created,
      }),
    };
  }
  return {
    label: t('shopifyConnection.badge.inviteUsed'),
    tone: 'neutral',
    detail: t('shopifyConnection.detail.inviteUsed', {
      consumed: formatMoment(invitation.consumedAt) ?? created,
    }),
  };
}

function statusPresentation(
  t: TFunction,
  invitation: Invitation | undefined,
  nowMs: number,
): Presentation {
  const installation = invitation?.installation ?? null;
  if (!installation) return invitationPresentation(t, invitation, nowMs);
  const domain = installation.canonicalDomain;

  if (installation.legacyCutoverRequired) {
    return {
      label: t('shopifyConnection.badge.upgradePaused'),
      tone: 'amber',
      detail: t('shopifyConnection.detail.upgradePaused', { domain }),
    };
  }

  switch (installation.status) {
    case 'active':
      return {
        label: t('shopifyConnection.badge.connected'),
        tone: 'emerald',
        detail: t('shopifyConnection.detail.connected', { domain }),
      };
    case 'initializing':
      return {
        label: t('shopifyConnection.badge.initialSync'),
        tone: 'neutral',
        detail: t('shopifyConnection.detail.initialSync', { domain }),
        inProgress: true,
      };
    case 'needs_reauth':
    case 'error':
      return {
        label: t('shopifyConnection.badge.needsAttention'),
        tone: 'amber',
        detail: t('shopifyConnection.detail.needsAttention', { domain }),
      };
    case 'uninstalled':
      return {
        label: t('shopifyConnection.badge.uninstalled'),
        tone: 'amber',
        detail: t('shopifyConnection.detail.uninstalled', { domain }),
      };
    case 'redacting':
      return {
        label: t('shopifyConnection.badge.removingData'),
        tone: 'red',
        detail: t('shopifyConnection.detail.removingData', { domain }),
      };
    case 'disconnected':
      return {
        label: t('shopifyConnection.badge.disconnected'),
        tone: 'neutral',
        detail: t('shopifyConnection.detail.disconnected', { domain }),
      };
    case 'unlinked':
    default:
      return invitationPresentation(t, invitation, nowMs);
  }
}

async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/** Provider-side controls for one existing client/location junction. */
export function ShopifyConnectionPanel({
  locationId,
  clientOrganizationId,
  clientName,
}: ShopifyConnectionPanelProps) {
  const { t } = useTranslation('settings');
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [displayOnceInvite, setDisplayOnceInvite] = useState<DisplayOnceInvite | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const copyResetTimer = useRef<number | null>(null);
  const utils = trpc.useUtils();
  const query = trpc.shopifyConnections.list.useQuery(undefined, {
    staleTime: 15_000,
    retry: false,
  });

  const matches = useMemo(
    () => [...(query.data?.invitations ?? [])]
      .filter((item) =>
        item.locationId === locationId
        && item.targetClientOrganizationId === clientOrganizationId)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [clientOrganizationId, locationId, query.data?.invitations],
  );
  const pendingInvitation = matches.find((item) => item.state === 'pending');
  const installationInvitation = matches.find((item) => item.installation !== null);
  const headlineInvitation = installationInvitation ?? pendingInvitation ?? matches[0];
  const installation = installationInvitation?.installation ?? null;

  // One scheduled re-render at the moment a live code expires, so the badge and
  // the one-time panel stop claiming a dead code is usable.
  const nextExpiryAt = useMemo(() => {
    const stamps = [displayOnceInvite?.expiresAt, pendingInvitation?.expiresAt]
      .map((value) => (value ? Date.parse(value) : Number.NaN))
      .filter((value) => Number.isFinite(value));
    return stamps.length > 0 ? Math.min(...stamps) : null;
  }, [displayOnceInvite?.expiresAt, pendingInvitation?.expiresAt]);
  const [expiryTick, setExpiryTick] = useState(0);
  useEffect(() => {
    if (nextExpiryAt === null) return;
    const remaining = nextExpiryAt - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setExpiryTick((value) => value + 1), remaining + 250);
    return () => window.clearTimeout(timer);
  }, [nextExpiryAt, expiryTick]);

  const nowMs = Date.now();
  const presentation = statusPresentation(t, headlineInvitation, nowMs);
  const missingScopes = installation
    ? installation.requestedScopes.filter((scope) => !installation.grantedScopes.includes(scope))
    : [];
  const missingScopePhrases = missingScopes.map((scope) => scopePhrase(t, scope));

  const createMutation = trpc.shopifyConnections.createInvite.useMutation({
    onSuccess: (result) => {
      setDisplayOnceInvite({
        invitationId: result.invitationId,
        pairingCode: result.pairingCode,
        expiresAt: result.expiresAt,
        installUrl: result.installUrl,
        shownAt: Date.now(),
      });
      setCodeCopied(false);
      setOpen(true);
      toast({
        title: t('shopifyConnection.toast.inviteReadyTitle'),
        description: t('shopifyConnection.toast.inviteReadyBody'),
      });
      void utils.shopifyConnections.list.invalidate();
    },
    onError: (error) => {
      toast({
        title: t('shopifyConnection.toast.inviteFailedTitle'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const revokeMutation = trpc.shopifyConnections.revokeInvite.useMutation({
    onSuccess: () => {
      setConfirmingRevoke(false);
      setDisplayOnceInvite(null);
      toast({ title: t('shopifyConnection.toast.revokedTitle') });
      void utils.shopifyConnections.list.invalidate();
    },
    onError: (error) => {
      toast({
        title: t('shopifyConnection.toast.revokeFailedTitle'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const disconnectMutation = trpc.shopifyConnections.disconnect.useMutation({
    onSuccess: () => {
      setConfirmingDisconnect(false);
      setDisplayOnceInvite(null);
      toast({
        title: t('shopifyConnection.toast.disconnectedTitle'),
        description: t('shopifyConnection.toast.disconnectedBody'),
      });
      void utils.shopifyConnections.list.invalidate();
    },
    onError: (error) => {
      toast({
        title: t('shopifyConnection.toast.disconnectFailedTitle'),
        description: error.message,
        variant: 'destructive',
      });
      void utils.shopifyConnections.list.invalidate();
    },
  });

  // A one-time code survives only as long as the server still calls it pending.
  // Anything later (consumed, revoked, replaced) retires the panel so a dead
  // secret is never left on screen for the rest of the session.
  useEffect(() => {
    if (!displayOnceInvite) return;
    if (!query.isSuccess || query.dataUpdatedAt <= displayOnceInvite.shownAt) return;
    const stillPending = matches.some((item) =>
      item.id === displayOnceInvite.invitationId && item.state === 'pending');
    if (!stillPending) setDisplayOnceInvite(null);
  }, [displayOnceInvite, matches, query.dataUpdatedAt, query.isSuccess]);

  useEffect(() => () => {
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
  }, []);

  // Loading and error are NOT "the feature is off". Rendering nothing for
  // either makes a failed fetch indistinguishable from a dark feature flag.
  if (query.isPending) {
    return (
      <div
        className="mt-3 rounded-md border bg-muted/20"
        role="status"
        aria-busy="true"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Link2 className="h-4 w-4 shrink-0 text-threadheads-purple" aria-hidden="true" />
            <span className="text-sm font-medium">{t('shopifyConnection.title')}</span>
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
        <span className="sr-only">{t('shopifyConnection.loading', { client: clientName })}</span>
      </div>
    );
  }

  // No data at all: the status is genuinely unknown, so say that plainly.
  // (A failed REFRESH that still has cached data keeps the panel and gets the
  // stale-data strip below instead, rather than hiding a state we do know.)
  if (!query.data) {
    return (
      <div
        role="status"
        className="mt-3 flex flex-wrap items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-950"
      >
        <div className="flex min-w-0 gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('shopifyConnection.loadFailedTitle')}</p>
            <p className="text-xs">
              {t('shopifyConnection.loadFailedBody', { client: clientName })}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 border-amber-300 bg-white/70 text-xs text-amber-900 hover:bg-amber-100 hover:text-amber-900"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {query.isFetching
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
          {query.isFetching
            ? t('shopifyConnection.retrying')
            : t('shopifyConnection.retry')}
        </Button>
      </div>
    );
  }

  // A dark backend feature flag removes the affordance altogether. This lets
  // the implementation ship before Dev Dashboard and listing values exist.
  if (!query.data.enabled) return null;

  const busy = createMutation.isPending
    || revokeMutation.isPending
    || disconnectMutation.isPending;
  const pendingIsLive = pendingInvitation !== undefined
    && Date.parse(pendingInvitation.expiresAt) > nowMs;
  const revokableInvitationId = pendingIsLive ? pendingInvitation.id : null;
  const visibleCode = displayOnceInvite !== null
    && Date.parse(displayOnceInvite.expiresAt) > nowMs
    ? displayOnceInvite
    : null;
  const installUrl = displayOnceInvite?.installUrl ?? query.data.installUrl;
  const hasLiveInstallation = installation !== null
    && LIVE_INSTALLATION_STATUSES.includes(installation.status);
  const isRedacting = installation?.status === 'redacting';

  return (
    <div
      className="mt-3 rounded-md border bg-muted/20"
      role="group"
      aria-label={t('shopifyConnection.sectionLabel', { client: clientName })}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Link2 className="h-4 w-4 shrink-0 text-threadheads-purple" aria-hidden="true" />
          <span className="text-sm font-medium">{t('shopifyConnection.title')}</span>
          <Badge className={`${BADGE_BASE} ${BADGE_TONE[presentation.tone]}`}>
            {presentation.inProgress && (
              <Loader2
                className="h-3 w-3 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            {presentation.label}
          </Badge>
          {missingScopePhrases.length > 0 && (
            <Badge className={`${BADGE_BASE} ${BADGE_TONE.amber}`}>
              {t('shopifyConnection.badge.permissionsChanged')}
            </Badge>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-3 text-xs"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t('shopifyConnection.hide') : t('shopifyConnection.manage')}
        </Button>
      </div>

      {query.isError && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-300 bg-amber-50 px-3 py-2 text-amber-950"
        >
          <p className="text-xs">{t('shopifyConnection.refreshFailed')}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-amber-300 bg-white/70 px-2 text-xs text-amber-900 hover:bg-amber-100 hover:text-amber-900"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            {query.isFetching
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
            {query.isFetching ? t('shopifyConnection.retrying') : t('shopifyConnection.retry')}
          </Button>
        </div>
      )}

      <div id={panelId} hidden={!open}>
        {open && (
          <div className="space-y-3 border-t px-3 py-3">
            <div>
              <p className="text-sm text-foreground">{presentation.detail}</p>
              {installation && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatMoment(installation.lastSyncAt) === null
                    ? t('shopifyConnection.neverSynced')
                    : t('shopifyConnection.lastSync', {
                      when: formatMoment(installation.lastSyncAt),
                    })}
                </p>
              )}
              {installation?.lastErrorCode && (
                // The stored code is internal grammar, never rendered. What the
                // operator needs is the plain fact that the attempt failed.
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('shopifyConnection.lastAttemptFailed')}
                </p>
              )}
            </div>

            {missingScopePhrases.length > 0 && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p className="text-xs">
                  {t('shopifyConnection.scopes.missing', {
                    permissions: missingScopePhrases.join(', '),
                  })}
                </p>
              </div>
            )}

            {installation?.legacyCutoverRequired && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('shopifyConnection.legacy.title')}</p>
                  <p className="text-xs">{t('shopifyConnection.legacy.body')}</p>
                </div>
              </div>
            )}

            {visibleCode && (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-amber-950">
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  <p className="text-xs font-medium">
                    {t('shopifyConnection.oneTime.heading', {
                      expires: formatMoment(visibleCode.expiresAt) ?? '',
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="select-all rounded border bg-white px-2.5 py-1.5 font-mono text-sm tracking-wider text-foreground">
                    {visibleCode.pairingCode}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 text-xs"
                    onClick={() => {
                      void copyToClipboard(visibleCode.pairingCode).then((copied) => {
                        if (!copied) {
                          toast({
                            title: t('shopifyConnection.copy.codeFailedTitle'),
                            description: t('shopifyConnection.copy.codeFailedBody'),
                            variant: 'destructive',
                          });
                          return;
                        }
                        setCodeCopied(true);
                        if (copyResetTimer.current !== null) {
                          window.clearTimeout(copyResetTimer.current);
                        }
                        copyResetTimer.current = window.setTimeout(
                          () => setCodeCopied(false),
                          1400,
                        );
                      });
                    }}
                  >
                    {codeCopied
                      ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                    {codeCopied
                      ? t('shopifyConnection.oneTime.copied')
                      : t('shopifyConnection.oneTime.copyCode')}
                  </Button>
                </div>
                <p className="text-xs text-amber-950/80">{t('shopifyConnection.oneTime.help')}</p>
              </div>
            )}

            {pendingIsLive && !visibleCode && (
              <p className="text-xs text-muted-foreground">
                {t('shopifyConnection.pendingHidden')}
              </p>
            )}

            {!installUrl && !hasLiveInstallation && (
              <p className="text-xs text-muted-foreground">
                {t('shopifyConnection.installLinkMissing')}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {!isRedacting && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 text-xs"
                  disabled={busy}
                  onClick={() => createMutation.mutate({
                    locationId,
                    targetClientOrganizationId: clientOrganizationId,
                  })}
                >
                  {createMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
                  {pendingIsLive
                    ? t('shopifyConnection.actions.replaceInvite')
                    : t('shopifyConnection.actions.invite')}
                </Button>
              )}

              {installUrl && !isRedacting && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 gap-1.5 text-xs"
                    onClick={() => {
                      void copyToClipboard(installUrl).then((copied) => {
                        toast(copied
                          ? { title: t('shopifyConnection.copy.linkCopied') }
                          : {
                            title: t('shopifyConnection.copy.linkFailedTitle'),
                            description: t('shopifyConnection.copy.linkFailedBody'),
                            variant: 'destructive',
                          });
                      });
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('shopifyConnection.copy.copyInstallLink')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 gap-1.5 text-xs"
                    onClick={() => window.open(installUrl, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('shopifyConnection.copy.openInstallPage')}
                  </Button>
                </>
              )}

              {revokableInvitationId && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 border-orange-300 text-xs text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                  disabled={busy}
                  onClick={() => setConfirmingRevoke(true)}
                >
                  {t('shopifyConnection.actions.revokeInvite')}
                </Button>
              )}

              {installation && hasLiveInstallation && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 border-orange-300 text-xs text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                  disabled={busy}
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  {t('shopifyConnection.actions.disconnect')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Revoking kills a code the merchant may be typing right now: same
          two-step gate as Disconnect, §13.6 orange confirm (both reversible). */}
      <AlertDialog
        open={confirmingRevoke}
        onOpenChange={(next) => !next && setConfirmingRevoke(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shopifyConnection.revokeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('shopifyConnection.revokeDialog.body', { client: clientName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>
              {t('shopifyConnection.revokeDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="border-orange-300 bg-transparent text-orange-700 hover:bg-orange-50 hover:text-orange-700"
              disabled={revokeMutation.isPending || revokableInvitationId === null}
              onClick={(event) => {
                event.preventDefault();
                if (revokableInvitationId === null) return;
                revokeMutation.mutate({ invitationId: revokableInvitationId });
              }}
            >
              {revokeMutation.isPending
                ? t('shopifyConnection.revokeDialog.pending')
                : t('shopifyConnection.revokeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmingDisconnect}
        onOpenChange={(next) => !next && setConfirmingDisconnect(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('shopifyConnection.disconnectDialog.title', {
                domain: installation?.canonicalDomain ?? clientName,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('shopifyConnection.disconnectDialog.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnectMutation.isPending}>
              {t('shopifyConnection.disconnectDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="border-orange-300 bg-transparent text-orange-700 hover:bg-orange-50 hover:text-orange-700"
              disabled={disconnectMutation.isPending || installation === null}
              onClick={(event) => {
                event.preventDefault();
                if (!installation) return;
                disconnectMutation.mutate({ installationId: installation.id });
              }}
            >
              {disconnectMutation.isPending
                ? t('shopifyConnection.disconnectDialog.pending')
                : t('shopifyConnection.disconnectDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
